import { BlueButton } from '../../../../customComponents/buttons/BlueButton'
import { AddCircle, CalendarMonth } from '@mui/icons-material'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useState } from 'react'
import { type StartTrainingAttendancePlan } from '../../../../../models/StartTrainingAttendancePlan'
import { StartTrainingAttendancePlanModal } from './StartTrainingAttendancePlanModal'
import { BannerModal } from '../../../../customComponents/BannerModal'
import {
  useStartTrainingAttendancePlanModal,
  useStartTrainingAttendancePlans
} from '../../../../../hooks/use-startTrainingAttendancePlans'
import { DeleteStartTrainingAttendancePlan, DeleteStartTrainingAttendancePlanTracking, TrackStartTrainingAttendancePlan } from '../../../../../services/AnnualPlanService'
import { toast } from 'react-toastify'
import { type ThingsToConsiderSection } from '../ThingsToConsiderCard'
import { BasicTable, type BasicTableColumn } from '../BasicTable'
import { ThingsToConsiderButton } from '../ThingsToConsiderButton'
import { AttendancePlanTrackingModal } from './AttendancePlanTrackingModal'
import { SubBasicTable } from '../SubBasicTable'

interface Props {
  priorityId: number
  annualPlanId: number
  rcnId: number
  readOnly: boolean
  trackingReadonly: boolean
  showTableOnly?: boolean
  hideExpand?: boolean
  implementationPhase: boolean
  budgetOverviewView?: boolean
}

export const StartTrainingAttendancePlanList = (props: Props): JSX.Element => {
  const [showTrackingModal, setShowTrackingModal] = useState(false)
  const [planBeingEdited, setPlanBeingEdited] = useState<StartTrainingAttendancePlan | null>(null)

  const {
    isLoading,
    startTrainingAttendancePlans,
    reloadTrainingAttendancePlans
  } = useStartTrainingAttendancePlans(props.priorityId, props.implementationPhase)
  const {
    initialValues,
    handleCloseModal,
    showTrainingAttendancePlanModal,
    handleAddTrainingAttendancePlan,
    handleEditTrainingAttendancePlan
  } = useStartTrainingAttendancePlanModal(props.implementationPhase)

  const [trainingAttendancePlanToDelete, setTrainingAttendancePlanToDelete] = useState<StartTrainingAttendancePlan | null>(null)
  const handleDeleteTrainingAttendancePlan = async (trainingAttendancePlan: StartTrainingAttendancePlan): Promise<void> => {
    const { success, errors } = await DeleteStartTrainingAttendancePlan(trainingAttendancePlan.id, trainingAttendancePlan.isUpdate)

    if (!success) {
      errors.forEach(e => toast.error(e))
      return
    }

    await reloadTrainingAttendancePlans()
    toast.success('Attendance Plan Deleted')
    setTrainingAttendancePlanToDelete(null)
  }

  const handleTrackTrainingAttendancePlan = async (startTrainingAttendancePlan: StartTrainingAttendancePlan): Promise<void> => {
    const dollarAmount: string | undefined = startTrainingAttendancePlan.actualDollarAmount == null
      ? undefined
      : startTrainingAttendancePlan.actualDollarAmount?.toString().replace('$', '')
    setPlanBeingEdited({ ...startTrainingAttendancePlan, actualDollarAmount: dollarAmount })
    setShowTrackingModal(true)
  }

  const deleteTracking = async (startTrainingAttendancePlan: StartTrainingAttendancePlan): Promise<void> => {
    await DeleteStartTrainingAttendancePlanTracking(startTrainingAttendancePlan.id)
    await reloadTrainingAttendancePlans()
    toast.success('Attendance Plan Tracking Deleted')
  }

  const saveTrackingPlanBeingEdited = async (attendees: number, actualBudget: number | null): Promise<void> => {
    if (planBeingEdited != null) {
      const budgetRequestAmount = planBeingEdited.budgetRequest.toString().replace('$', '')
      const startTrainingAttendancePlan = {
        ...planBeingEdited,
        expandedContent: undefined,
        budgetRequest: budgetRequestAmount,
        actualAttendeeCount: attendees,
        actualDollarAmount: actualBudget?.toString() ?? ''
      }

      setShowTrackingModal(false)
      const { success, errors } = await TrackStartTrainingAttendancePlan(startTrainingAttendancePlan)

      if (!success) {
        errors.forEach(e => toast.error(e))
        return
      }
      await reloadTrainingAttendancePlans()
      toast.success('Attendance Plan Tracked')
    }
  }

  const thingsToConsiderSections: ThingsToConsiderSection[] = [
    {
      title: '',
      items: [
        <>Review the <a style={{ color: 'currentcolor' }} target='_blank' href='https://www.gvsu.edu/autismcenter/paths-to-learning-539.htm'>Paths to Learning on the START website</a> to see the trainings and training series scheduled for next year.</>,
        'You can share the START Training opportunities throughout your districts since START Training is for all school staff, families, and caregivers.',
        'Recruit classroom teams to participate in the CETA training series.',
        'Review your Peer to Peer Data to determine whether specific district teams would benefit from attending Peer to Peer.',
        'Connect with your early childhood programs to determine interest in the Early Childhood training series.'
      ]
    }
  ]

  const isEmpty = startTrainingAttendancePlans.filter(plan => !plan.deleted).length === 0
  const columns: BasicTableColumn[] = [
    { id: 'trainingTitle', label: 'Training Title', minWidth: 200 },
    { id: 'numberOfAttendees', label: 'Attendance Count', minWidth: 200 },
    { id: 'budgetRequest', label: 'Budget', minWidth: 100 }
  ]
  const rows = startTrainingAttendancePlans.filter(plan => !plan.deleted).map(r => ({
    ...r,
    isUpdated: r.isUpdate,
    isTracked: r.actualAttendeeCount != null,
    trainingTitle: r.startTraining.title,
    budgetRequest: `$${r.budgetRequest}`,
    expandedContent: (r.actualAttendeeCount != null &&
      (
        <Box
          sx={{
            p: '2rem',
            backgroundColor: 'var(--grey-100)'
          }}
        >
          <SubBasicTable
            hasTrackingDetails
            readonly={props.readOnly}
            trackingReadonly={props.trackingReadonly}
            onEditTracking={handleTrackTrainingAttendancePlan}
            onDeleteTracking={deleteTracking}
            columns={[
              { id: 'actualAttendeeCount', label: 'ACTUAL ATTENDANCE COUNT', minWidth: 200, isTrackingColumn: true },
              { id: 'actualDollarAmount', label: 'ACTUAL DOLLAR AMOUNT', minWidth: 200, isTrackingColumn: true },
              { id: 'trackingAction', label: '', isTrackingColumn: true }
            ]}
            rows={[{
              ...r,
              id: r.id,
              actualAttendeeCount: r.actualAttendeeCount ?? 0,
              actualDollarAmount: `$${r.actualDollarAmount ?? 0}`
            }]}
            dataTestId={`${r.id}-expandedTAPTable`}
          />
        </Box>
      )
    )
  }))

  return <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }} data-testid='startTrainingAttendancePlanList'>
    <Typography variant='h4'>START Trainings and Training Series</Typography>

    {props.showTableOnly !== true && (
      <Box sx={{ width: '100%', alignItems: 'center' }}>
        {!props.readOnly &&
          <BlueButton onClick={handleAddTrainingAttendancePlan} dataTestId='addTrainingAttendancePlanButton'>
            Add Attendance Plan <AddCircle sx={{ ml: '10px' }} fontSize='small' />
          </BlueButton>
      }

        <Box sx={{ float: 'right' }}>
          <ThingsToConsiderButton
            buttonDataTestId='attendancePlanThingsToConsiderButton'
            thingsToConsiderSections={thingsToConsiderSections}
            cardDataTestId='attendancePlanThingsToConsiderCard'
          />
        </Box>
      </Box>
    )}

    <Box sx={{
      border: '1px solid var(--blue-300)',
      backgroundColor: 'var(--white)',
      borderRadius: '8px',
      padding: isEmpty ? '24px' : '0',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0px 10px 10px -8px #3B353E4D'
    }}
    >
      {isLoading
        ? <CircularProgress sx={{ alignSelf: 'center' }} />
        : (isEmpty
            ? <>
              <CalendarMonth sx={{ color: 'var(--blue-300)', width: '144px', height: '144px', alignSelf: 'center' }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
                <Typography variant='h4'>No Attendance Plan Added</Typography>
                {!props.readOnly &&
                  <Typography variant='body1' fontSize='16px'>
                    Use the Add Attendance Plan button to add a START Trainings and Training Series Attendance Plan to your Annual Plan.
                  </Typography>
                }
              </Box>
            </>
            : <BasicTable
                dataTestId='startTrainingAttendancePlanTable'
                columns={columns}
                indexRowIds={props.implementationPhase}
                rows={rows}
                isLoading={isLoading}
                readonly={props.readOnly}
                trackingReadonly={props.trackingReadonly}
                onEdit={handleEditTrainingAttendancePlan}
                onUpdate={handleEditTrainingAttendancePlan}
                onDelete={setTrainingAttendancePlanToDelete}
                hideExpand={props.hideExpand}
                implementationPhase={props.implementationPhase}
                onTrack={handleTrackTrainingAttendancePlan}
                budgetOverviewView={props.budgetOverviewView}
            />)
      }

      {showTrainingAttendancePlanModal &&
        <StartTrainingAttendancePlanModal
          usedIds={startTrainingAttendancePlans.filter(plan => !plan.deleted).map(r => r.startTraining.id ?? -1)}
          isImplementationPhase={props.implementationPhase ?? false}
          open={showTrainingAttendancePlanModal}
          priorityId={props.priorityId}
          onClose={handleCloseModal}
          onSaved={reloadTrainingAttendancePlans}
          initialValues={initialValues}
          annualPlanId={props.annualPlanId}
          rcnId={props.rcnId}
        />
      }

      {trainingAttendancePlanToDelete != null &&
        <BannerModal
          title='Are you sure?'
          cancelButtonText='No, Cancel'
          confirmButtonText='Yes, Delete'
          dataTestId='confirmDeleteTrainingAttendancePlanModal'
          bodyContent={`Are you sure you want to delete this item?${trainingAttendancePlanToDelete.actualAttendeeCount != null ? ' This will also remove any tracking data associated with it.' : ''}`}
          onClose={() => { setTrainingAttendancePlanToDelete(null) }}
          onConfirm={() => { void handleDeleteTrainingAttendancePlan(trainingAttendancePlanToDelete) }}
          maxWidth='xs'
          bottom=''
          position='fixed'
        />
      }

      {showTrackingModal &&
        <AttendancePlanTrackingModal
          title={`${planBeingEdited?.startTraining.title ?? 'Event Attendance'} Tracking`}
          onSave={saveTrackingPlanBeingEdited}
          attendees={planBeingEdited?.actualAttendeeCount?.toString() ?? ''}
          budget={planBeingEdited?.actualDollarAmount ?? ''}
          onCancel={() => { setShowTrackingModal(false) }}
        />
      }
    </Box>
  </Box>
}
